import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  IconButton,
  InputAdornment,
  CircularProgress
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import "./LoginPage.scss";
import Api from "../../utils/api";
import FTLogo from "../../assets/images/FTLogo.svg";
import { useNavigate } from "react-router-dom";
import { useError } from "../../contexts/ErrorContext";

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [csrfToken, setCsrfToken] = useState("");
  const [loading, setLoading] = useState(false); // Loader state
  const navigate = useNavigate();
  const { showError } = useError();

  useEffect(() => {
    const handleGetCSRFToken = async () => {
      const csrfResult = await Api.getCSRFToken();
      const { csrf_token } = csrfResult;
      setCsrfToken(csrf_token);
    };
    handleGetCSRFToken();
  }, []);

  const handleLogin = async () => {
    try {
      if (!email || !password) {
        return;
      }

      setLoading(true);

      const authResult = await Api.handleLogin(email, password, csrfToken);
      if (authResult?.AuthenticationResult?.ErrorDescription) {
        showError(authResult.AuthenticationResult.ErrorDescription);
      } else if (authResult?.AuthenticationResult) {
        const { AccessToken, RefreshToken, ExpiresIn } = authResult.AuthenticationResult;

        localStorage.setItem("accessToken", AccessToken);
        localStorage.setItem("refreshToken", RefreshToken);
        localStorage.setItem("expiresAt", (Date.now() + ExpiresIn * 1000).toString());

        console.log("Tokens stored successfully.");
        navigate("/");
      } else {
        console.error("Invalid authentication result.");
      }
    } catch (e: any) {
      showError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-screen">
      {/* Left Section */}
      <div className="left-section">
        <div className="text-section">
          <h1>Welcome To</h1>
          <h2>GOE Developer Hub</h2>
          <p>Powered By Franklin Templeton</p>
        </div>
      </div>

      {/* Right Section with MUI */}
      <div className="right-section flex justify-center items-center w-1/2">
        <Card sx={{ width: 400, p: 3, boxShadow: 3, textAlign: "center" }}>
          <CardContent>
            <img src={FTLogo} alt="Franklin Templeton" style={{ width: "120px", marginBottom: "10px" }} />
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Login
            </Typography>

            <form
              autoComplete="off"
              onSubmit={(e) => {
                e.preventDefault();
                handleLogin();
              }}
            >
              {/* Hidden CSRF token field */}
              <input type="hidden" name="csrfToken" value={csrfToken} />

              <TextField
                fullWidth
                autoComplete="off"
                variant="outlined"
                label="Email"
                margin="normal"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />

              <TextField
                fullWidth
                autoComplete="off"
                variant="outlined"
                label="Password"
                margin="normal"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword((prev) => !prev)} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <FormControlLabel
                control={<Checkbox color="primary" />}
                label="Remember Me"
                sx={{ textAlign: "left", width: "100%", mt: 1 }}
              />

              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                sx={{ mt: 2 }}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : "Login"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;
